import React, { useState } from 'react';
import { Box, Typography, Alert } from "@mui/material";
import FileUploader from "../../../../../shared/components/inputfields/useDropzone";
import { useApi } from "../../../../../shared/hooks/useApi";
import useNotification from "../../../../../shared/hooks/useNotification";
import { LoaderComponent } from "../../../../../shared/components/loaders/page-loader";
import { MLAppConfig } from "../../../../../shared/app-configs/ml-app-configs";
import { useTransactionCertificateStore } from "../../../../../store/transaction-certificates.store";
import { StyledButton } from "../../../../../shared/components/buttons/custom-button";
import { ReactComponent as Loader } from "../../../../../assests/logos/loader.svg";

interface InventoryDocumentsProps {
  formRef: any;
  documentsData: any[];
  setDocumentsData: (data: any) => void;
  loading: boolean;
  setFileName: (fileName: string[]) => void;
  setFilePath: (filePath: string[]) => void;
  refreshInventory:()=>void;
  coTEId: string;
  organizationName: string;
  isMultiBulkUpload?: boolean;
}

const InventoryDocuments = ({ 
  formRef, 
  documentsData, 
  setDocumentsData, 
  loading,
  refreshInventory,
  setFileName,
  setFilePath,
  coTEId,
  organizationName,
  isMultiBulkUpload = false
}: InventoryDocumentsProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloadingFormat, setIsDownloadingFormat] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'success' | 'error' | 'validation_error'>('idle');
  const [errorDetails, setErrorDetails] = useState<{
    message: string, 
    fileUrl: string,
    filename?: string
  } | null>(null);
  const { executeGet, executePost } = useApi();
  const { notify } = useNotification();
  const { cbLicensingCode } = useTransactionCertificateStore();

  // State to track uploaded files for batch processing
  const [uploadedFilePaths, setUploadedFilePaths] = useState<string[]>([]);
  const [totalFilesToUpload, setTotalFilesToUpload] = useState<number>(0);

  // Handle successful upload from FileUploader
  const handleUploadSuccess = async (fileInfo: { fileName: string; file: File }) => {
    // Add to documents data
    setDocumentsData((prev: any[]) => [...prev, fileInfo.file]);
    
    // Call processFiles with array containing single filename
    await processFiles([fileInfo.fileName]);
    
    // notify(`File ${fileInfo.fileName} uploaded successfully.`, "success");
  };

  // Process files with the provided file paths array
  const processFiles = async (filePaths: string[]) => {
    if (filePaths.length === 0) {
      return;
    }

    setIsUploading(true);
    setUploadStatus('idle');

    try {
      // Build query parameters based on upload mode
      let queryParams: any;
      
      if (isMultiBulkUpload) {
        // For multi bulk upload: only include files and cbLicensingCode
        queryParams = new URLSearchParams({
          files: JSON.stringify(filePaths),
          tcCbLicensingCode: cbLicensingCode
        });
      } else {
        // For regular bulk upload: include teId, files, and cbLicensingCode
        const teIdWithPrefix = `TE-${coTEId}`;
        queryParams = new URLSearchParams({
          teId: teIdWithPrefix,
          files: JSON.stringify(filePaths),
          tcCbLicensingCode: cbLicensingCode
        });
      }
      
      const finalUrl = `${MLAppConfig.PROCESS_EXCEL_DATA}?${queryParams}`;
      const processResponse = await executePost(finalUrl, {}, {
        responseType: 'arraybuffer',
        headers: {
          'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/json'
        }
      });

      if (processResponse?.status === 200) {
        setUploadStatus('success');
        notify(`${filePaths.length} file(s) processed successfully.`, "success");
        // Clear uploaded files after successful processing
        setUploadedFilePaths([]);
        refreshInventory();
        setErrorDetails(null);
      } else if (processResponse?.status === 400) {
        // Handle validation errors - binary Excel file response
        setUploadStatus('validation_error');
        
        // The response data is already an ArrayBuffer containing the Excel file
        const excelArrayBuffer = processResponse.data;
        const filename = 'error_report.xlsx';
        const contentType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        
        // Create blob from ArrayBuffer
        const blob = new Blob([excelArrayBuffer], { type: contentType });
        const downloadUrl = URL.createObjectURL(blob);
        
        setErrorDetails({
          message: 'Validation errors found',
          fileUrl: downloadUrl,
          filename: filename
        });
        
        // Clear uploaded files
        setUploadedFilePaths([]);
      } else {
        throw new Error('Failed to start file processing');
      }

    } catch (error) {
      console.error('File processing error:', error);
      setUploadStatus('error');
      notify(`Processing failed: ${error instanceof Error ? error.message : 'Unknown error'}`, "error");
    } finally {
      setIsUploading(false);
    }
  };

  // Handle upload error from FileUploader
  const handleUploadError = (error: string) => {
    console.error('File upload error:', error);
    setUploadStatus('error');
    notify(`Upload failed: ${error}`, "error");
  };

  // Download error file
  const downloadErrorFile = async (fileUrl: string, filename?: string) => {
    try {
      setIsUploading(true);
      
      // Create download link
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = filename || 'error_report.xlsx';
      link.style.display = 'none';
      document.body.appendChild(link);
      
      link.click();
      
      // Clean up after a short delay
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(fileUrl);
      }, 100);
      
      notify("Error file downloaded successfully", "success");
    } catch (error) {
      console.error('Download error:', error);
      notify(`Failed to download error file: ${error instanceof Error ? error.message : 'Unknown error'}`, "error");
    } finally {
      setIsUploading(false);
    }
  };

  // Download Excel template file
  const downloadFormatFile = async () => {
    try {
      setIsDownloadingFormat(true);
      
      // Build query params based on upload mode
      let queryParams: any = {};
      if (isMultiBulkUpload) {
        queryParams = { type: 'multi' };
      }
      
      const response = await executeGet(MLAppConfig.DOWNLOAD_EXCEL_TEMPLATE, queryParams, {
        responseType: 'arraybuffer',
        headers: {
          'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }
      });
      
      if (response?.status === 200) {
        // Create blob from ArrayBuffer
        const blob = new Blob([response.data], { 
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        });
        
        // Create download link
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = isMultiBulkUpload ? 'inventory_multi_template.xlsx' : 'inventory_template.xlsx';
        link.style.display = 'none';
        document.body.appendChild(link);
        
        link.click();
        
        // Clean up after a short delay
        setTimeout(() => {
          document.body.removeChild(link);
          URL.revokeObjectURL(link.href);
        }, 100);
        
        notify("Template file downloaded successfully", "success");
      } else {
        throw new Error('Failed to download template');
      }
    } catch (error) {
      console.error('Template download error:', error);
      notify(`Failed to download template: ${error instanceof Error ? error.message : 'Unknown error'}`, "error");
    } finally {
      setIsDownloadingFormat(false);
    }
  };

  // Reset to allow new upload
  const resetUploader = () => {
    setUploadStatus('idle');
    setErrorDetails(null);
    setUploadedFilePaths([]);
    setTotalFilesToUpload(0);
    setDocumentsData([]);
  };

  return (
    <Box>
      {/* Show organization details only for regular bulk upload (not multi bulk) */}
      {!isMultiBulkUpload && (
        <Box
          sx={{ mb: 2, p: 2, backgroundColor: "#f5f5f5", borderRadius: 1 }}
        >
          <Typography variant="body1" color="textSecondary">
            Organization: {organizationName} (TE ID: {coTEId})
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Typography variant="body2" color="textSecondary">
          {isMultiBulkUpload 
            ? 'Upload Excel (.xlsx) files for multiple TE-ID. Files will be processed automatically after upload.'
            : 'Upload Excel (.xlsx) files only. File will be processed automatically after upload.'
          }
        </Typography>
        <StyledButton
          variant="outlined"
          size="small"
          onClick={downloadFormatFile}
          disabled={isUploading || isDownloadingFormat}
        >
          Download Format File
        </StyledButton>
      </Box>

      {(isUploading || isDownloadingFormat) ? (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '300px',
          flexDirection: 'column'
        }}>
          <Loader style={{ width: 50, height: 50 }} />
          <Typography variant="body2" sx={{ mt: 2 }}>
            {isDownloadingFormat ? 'Downloading Input Format file...' : 'Processing files...'}
          </Typography>
        </Box>
      ) : (
        <>
          {uploadStatus === 'success' && (
            <Alert severity="success" sx={{ mb: 2 }}>
              File uploaded successfully! Processing has started.
            </Alert>
          )}

          {uploadStatus === 'error' && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Upload failed. Please try again.
            </Alert>
          )}

          {uploadStatus === 'validation_error' && errorDetails && (
            <Box sx={{ mb: 2 }}>
              <Alert severity="error" sx={{ mb: 2 }}>
                {errorDetails.message}
                <br />
              <Typography variant="body2" sx={{ mt: 1 }}>
                Click on the Download Error Report, Fix the errors and upload the updated file
              </Typography>
              </Alert>
            </Box>
          )}

          {uploadStatus !== 'validation_error' ? (
            <FileUploader 
              uniqueId={isMultiBulkUpload ? "multi-bulk-inventory-upload" : "bulk-inventory-upload"}
              setFileName={setFileName}
              setFilePath={setFilePath}
              onUploadSuccess={handleUploadSuccess}
              onUploadError={handleUploadError}
            />
          ) : null}

          {uploadStatus === 'validation_error' && errorDetails && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, pb: 2 }}>
              <StyledButton 
                variant="contained" 
                color="primary"
                onClick={() => downloadErrorFile(errorDetails.fileUrl, errorDetails.filename)}
                disabled={isUploading || isDownloadingFormat}
              >
                Download Error Report
              </StyledButton>
              <StyledButton 
                  variant="outlined" 
                  onClick={resetUploader}
                  disabled={isUploading || isDownloadingFormat}
                >
                  Upload New Files
                </StyledButton>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default InventoryDocuments;